import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Fragment } from "react";
import SVG from "react-inlinesvg";
import TextButton from "../TextButton";
import { useProjectStore } from "../../stores/projectStore.js";
import classNames from "classnames";

import styled from "styled-components/macro";
import { themeVariables } from "../../themeVariables.js";

const Container = styled.div`
  background-color: ${themeVariables.menuColorFullscreen};
  border-radius: 20px;
  box-shadow: var(--drop-shadow-15-25);
  margin-top: 20px;
  margin-bottom: 10px;
  padding: 18px;
  font-weight: 500;
  color: var(--grey-0);
  .character-offer-image {
    width: 300px;
    float: right;
    border: 1px solid var(--grey-3);
    border-radius: 20px;
    padding: 20px;
    margin-left: 20px;
  }
  .header-right {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
  }
  .upgrade-button {
    margin-top: 0 !important;
    border-radius: 10px !important;
    padding: 9px 14px !important;
    text-wrap: nowrap;
  }
  .explainer {
    margin-top: 1em;
  }
  .faq-button {
    background: none !important;
    color: var(--grey-0) !important;
    text-decoration: underline;
    border: none;
    padding: 0 !important;
    margin: 0 !important;
    margin-top: 1em !important;
  }
  .character-offer-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 1em;
    h1 {
      font-size: 20px;
      font-weight: 500;
      color: var(--grey-0);
    }
  }
  .checklist {
    tr {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    td {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 6px;
      svg {
        path {
          fill: var(--grey-0);
        }
      }
    }
  }
  .deferred-collapsed {
    margin-bottom: 0;
  }
  ul {
    margin-left: 1.5em;
    list-style-type: none;
    li {
      font-weight: 700;
    }
  }
  .offer-body p {
    margin-top: 1em;
  }
  .offer-body.single-column {
    display: flex;
    flex-direction: column;
    .character-offer-image {
      align-self: center;
      margin-left: 0px;
    }
  }
  .secondary {
    color: var(--primary-cta);
    border: 1px solid transparent !important;
    border-radius: 10px !important;
    padding: 7px 12px 7px 12px !important;
    margin-top: 0 !important;
  }
  sup {
    vertical-align: text-top;
    font-size: 0.6em;
  }
  .upgrade-container {
    position: relative;
    display: flex;
    flex-direction: column;
    .svg-container {
      float: right;
    }
    svg {
      position: absolute;
      right: 14px;
      top: -15px;
    }
  }
  .inline-svg {
    position: relative;
    top: 5px;
    path {
      fill: var(--grey-0);
    }
  }
  h2 {
    margin-top: 2em;
    margin-bottom: 12px !important;
    font-weight: 600;
  }
  h3 {
    margin-bottom: 0em;
    margin-top: 1em;
  }
  .q-or-a {
    font-weight: 600;
    color: var(--grey-1);
    padding-right: 10px;
    padding-top: 10px;
  }
  strong {
    font-weight: 600;
  }
  .beginning-of-question {
    padding-top: 25px;
  }
  table {
    line-height: 1.5em;
    /* width: 100%; */
  }
`;

// the size of the center column where we switch to single column layout for the image
const SINGLE_COLUMN_BREAKPOINT = 650;
function ProjectUpgradeOffer() {
  const [showFaq, setShowFaq] = useState(false);
  const project = useProjectStore((state) => state.project);
  const [optimisticDeferred, setOptimisticDeferred] = useState(project.deferProjectUpgrade);
  const [upgrading, setUpgrading] = useState(false);
  // goes to single center column at small widths
  const [singleColumn, setSingleColumn] = useState(false);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  useEffect(() => {
    if (columnWidths.center.current <= SINGLE_COLUMN_BREAKPOINT) {
      setSingleColumn(true);
    } else {
      setSingleColumn(false);
    }
  }, [columnWidths.center]);
  const handleNoThanks = async () => {
    setOptimisticDeferred(true);
    setShowFaq(false);
    await project.update({ deferProjectUpgrade: true });
  };
  const handleUpgrade = async () => {
    setUpgrading(true);
    await project.optInStructuredOutline();
    setUpgrading(false);
    window.location.reload();
  };
  if (project.useStructuredOutline) return null;

  return (
    <Container>
      <div className={classNames("character-offer-header", { "deferred-collapsed": optimisticDeferred && !showFaq })}>
        {!optimisticDeferred && <h1>Introducing Outline Unlimited</h1>}
        {optimisticDeferred && (
          <p>
            This project will automatically update to Outline Unlimited on December 9<sup style={{ verticalAlign: "super" }}>th</sup>
          </p>
        )}
        <div className="header-right">
          {!optimisticDeferred && (
            <button
              className="secondary"
              onClick={handleNoThanks}
              aria-label="Do not update to Story Bible 2 now, wait until June 15th to be automatically updated"
            >
              Wait until December 9<sup style={{ verticalAlign: "super" }}>th</sup>
            </button>
          )}
          {optimisticDeferred && <TextButton onClick={() => setShowFaq(!showFaq)}>Learn more...</TextButton>}
          <div className="upgrade-container">
            {!upgrading && (
              <button
                className="upgrade-button"
                disabled={upgrading}
                onClick={handleUpgrade}
                aria-label="update this project to use Story Bible 2 now"
              >
                Update Now
              </button>
            )}
            {upgrading && (
              <motion.button
                className="upgrade-button"
                initial={{
                  background: `linear-gradient(90deg,  var(--primary-cta) ${0}%, var(--grey-2) ${0}%)`,
                }}
                animate={{
                  background: `linear-gradient(90deg, var(--primary-cta)  90%, var(--grey-2) 90%)`,
                  transition: {
                    duration: 3,
                    ease: "linear",
                  },
                }}
              >
                Updating...
              </motion.button>
            )}
          </div>
        </div>
      </div>
      {!optimisticDeferred && (
        <div className={`offer-body ${singleColumn ? "single-column" : ""}`}>
          <img
            src="/assets/UnlimitedOutline.png"
            alt="Unlimited Outline Upgrade"
            className="character-offer-image"
          />
          <p>We heard your requests for a bigger, better Outline. We think you'll love these improvements:</p>
          <p>
            <table className="checklist">
              <tr>
                <td>
                  <SVG
                    src="/assets/check-simple.svg"
                    alt="Check"
                    aria-hidden="true"
                  />
                </td>
                <td>Outlines can be as long as you want</td>
              </tr>
              <tr>
                <td>
                  <SVG
                    src="/assets/check-simple.svg"
                    alt="Check"
                    aria-hidden="true"
                  />
                </td>
                <td>Re-order by dragging & dropping chapters</td>
              </tr>
              {!showFaq && (
                <tr>
                  <td>
                    <SVG
                      src="/assets/check-simple.svg"
                      alt="Check"
                      aria-hidden="true"
                    />
                  </td>
                  <td>More control when you Generate</td>
                </tr>
              )}
              {showFaq && (
                <>
                  <tr>
                    <td>
                      <SVG
                        src="/assets/check-simple.svg"
                        alt="Check"
                        aria-hidden="true"
                      />
                    </td>
                    <td>Create custom Outline structures</td>
                  </tr>
                  <tr>
                    <td>
                      <SVG
                        src="/assets/check-simple.svg"
                        alt="Check"
                        aria-hidden="true"
                      />
                    </td>
                    <td>Generate or rewrite one chapter at a time</td>
                  </tr>
                  <tr>
                    <td>
                      <SVG
                        src="/assets/check-simple.svg"
                        alt="Check"
                        aria-hidden="true"
                      />
                    </td>
                    <td>Support for Prologues, Epilogues, and anything else you'd want to put in an Outline</td>
                  </tr>
                  <tr>
                    <td>
                      <SVG
                        src="/assets/check-simple.svg"
                        alt="Check"
                        aria-hidden="true"
                      />
                    </td>
                    <td>Easily update your Outline as your drafts change with 1 click.</td>
                  </tr>
                </>
              )}
            </table>
          </p>
          {!showFaq && <p>... and lots more!</p>}
        </div>
      )}
      {showFaq && (
        <Fragment>
          <h2>Frequently Asked Questions</h2>
          <table>
            <tr>
              <td className="q-or-a">Q</td>
              <td>
                <strong>What will happen to my existing Outline?</strong>
              </td>
            </tr>
            <tr>
              <td className="q-or-a">A</td>
              <td>It will be converted into the new format without changes.</td>
            </tr>
            <tr>
              <td className="q-or-a beginning-of-question">Q</td>
              <td>
                <strong>What's better about the new Outline?</strong>
              </td>
            </tr>
            <tr>
              <td className="q-or-a">A</td>
              <td>
                Lots of things! You can write outlines that are much longer, choose from several outline types or design your own by describing what you want, re-order chapters easily, and…well, to be
                honest, it’s just all those things up above plus a million tiny improvements that you may or may not notice.
              </td>
            </tr>
            <tr>
              <td className="q-or-a beginning-of-question">Q</td>
              <td>
                <strong>How can I bring in my Outline from someplace else?</strong>
              </td>
            </tr>
            <tr>
              <td className="q-or-a">A</td>
              Use “Import Outline” in the “…” menu.
              <td></td>
            </tr>
            <tr>
              <td className="q-or-a beginning-of-question">Q</td>
              <td>
                <strong>
                  What happens if I wait until December 9<sup style={{ verticalAlign: "super" }}>th</sup>?
                </strong>
              </td>
            </tr>
            <tr>
              <td className="q-or-a">A</td>
              <td>Your project will be upgraded automatically at that time!</td>
            </tr>
            <tr>
              <td className="q-or-a beginning-of-question">Q</td>
              <td>
                <strong>What happens if I convert, but the outline is empty?</strong>
              </td>
            </tr>
            <tr>
              <td className="q-or-a">A</td>
              <td>
                Improperly formatted outlines cannot be converted. Your outline is still available, and will be in a document in the trash. If you choose, you can copy the text and import it using the
                import outline option in the outline "..." menu.
              </td>
            </tr>
          </table>
        </Fragment>
      )}
      {(!optimisticDeferred || showFaq) && (
        <button
          className="faq-button"
          onClick={() => setShowFaq(!showFaq)}
        >
          {showFaq ? "Hide" : "Learn more & Read our"} FAQ
        </button>
      )}
    </Container>
  );
}
export default ProjectUpgradeOffer;
